import { Article } from '../../types/strapi';

import {
  MarketUpdateRow,
  ResearchUpdateRow,
  toArticle,
  toMarketUpdate,
  toResearchUpdate,
} from './adapt';
import { ContentItem, fetchContentItem, fetchFeed, fetchOpenLane } from './contentApi';

export interface GetAllArticlesByCategoryProps {
  category: string | Array<string> | null;
  /**
   * The org's Compass project types. Only part of the cache key now: the server intersects the
   * article's own project_types against the org's real modules, so passing a wider list here
   * cannot widen what comes back.
   */
  projectTypes?: string[];
  /**
   * Whether the caller holds research_hub:research_list. With it the listing comes from the
   * entitlement-resolving feed and gated articles arrive as locked cards. Without it the open
   * lane is all there is, which is what the dashboard's insights card reads.
   */
  hasResearchListAccess?: boolean;
}

/**
 * public_only articles belong to the marketing site. The open lane carries them because the
 * website reads the same payload, so the platform drops them here.
 */
const isPlatformVisible = (item: ContentItem) => item.visibility !== 'public_only';

const byCategory = (
  articles: Article[],
  category: GetAllArticlesByCategoryProps['category']
): Article[] => {
  if (category === null) return articles;
  const wanted = (Array.isArray(category) ? category : [category]).map((c) => c.toLowerCase());
  return articles.filter((a) => wanted.includes((a.post_category?.url_slug ?? '').toLowerCase()));
};

const newestFirst = (a: Article, b: Article) =>
  new Date(b?.published_date).getTime() - new Date(a?.published_date).getTime();

/**
 * Every article the caller may see as a card. Compass narrowing and the subscriber_only
 * decision are the server's, so a gated article comes back locked rather than absent.
 */
export const getAllArticlesByCategory = async (
  config: GetAllArticlesByCategoryProps
): Promise<Article[]> => {
  const items = config.hasResearchListAccess
    ? await fetchFeed('article')
    : await fetchOpenLane(false);

  const articles = items.filter(isPlatformVisible).map(toArticle);
  return byCategory(articles, config.category).sort(newestFirst);
};

export const getArticle = async (slug: string): Promise<Article | undefined> => {
  const item = await fetchContentItem(slug, 'article');
  // public_only is the marketing site's; the page's not-found branch handles the gap.
  return item && isPlatformVisible(item) ? toArticle(item) : undefined;
};

/** Related content for a project detail page: pinned first, then newest. */
export const getArticlesByProjectType = async (projectType: string): Promise<Article[]> => {
  const items = await fetchFeed('article');
  return items
    .filter(isPlatformVisible)
    .map(toArticle)
    .filter((a) => a.project_types?.some((t) => t?.name === projectType))
    .sort((a, b) => {
      const pinned = Number(b.isPinnedRelatedContent) - Number(a.isPinnedRelatedContent);
      return pinned !== 0 ? pinned : newestFirst(a, b);
    });
};

export const getResearchUpdates = async (): Promise<ResearchUpdateRow[]> =>
  (await fetchFeed('research_update')).map(toResearchUpdate);

export const getResearchUpdate = async (date: string): Promise<ResearchUpdateRow | undefined> => {
  const item = await fetchContentItem(date, 'research_update');
  return item ? toResearchUpdate(item) : undefined;
};

export const getMarketUpdates = async (): Promise<MarketUpdateRow[]> =>
  (await fetchFeed('market_update')).map(toMarketUpdate);

export const getMarketUpdate = async (slug: string): Promise<MarketUpdateRow | undefined> => {
  const item = await fetchContentItem(slug, 'market_update');
  return item ? toMarketUpdate(item) : undefined;
};
